import React, { useEffect, useState, useCallback } from 'react';
import { Layout, Model, IJsonModel } from 'flexlayout-react';
import 'flexlayout-react/style/light.css';

import config from '../config';
import defaultConfig from '../data/default_configuration.json';
import { DownloadIcon, HomeIcon , PlayIcon , FileIcon, ExitIcon, UploadIcon, HeartIcon, RocketIcon, PlusCircledIcon, InfoCircledIcon, CaretDownIcon, Share1Icon} from "@radix-ui/react-icons";
import * as Menubar from '@radix-ui/react-menubar';

import { DocumentDataProvider, useDocumentData } from '../context/DocumentDataProvider';
import { dashboardContainerStyle, headerStyle, headerLeft, welcomeTitle, userEmail, headerRight, profilePicStyle, logoutButtonStyle } from './dashboardStyles';

// Importa tus componentes de pestañas
import ThreeDTab, {LoadedModel}  from './tabs/ThreeDTab';
import PlotlyTab from './tabs/PlotlyTab';
import TableTab from './tabs/TableTab';
import HeatMapTab from './tabs/HeatMapTab';
import HeatMapVideoTab from './tabs/HeatMapVideoTab';


import PropertyGridTab from './tabs/PropertyGridTab';
import ComponentSelectorModal from './ComponentSelectorModal';
import CategorySelectorModal from './CategorySelectorModal';
import RepresentationSelectorModal from './RepresentationSelectorModal';
import AboutModal from './AboutModal';
import ReactFlowTab from './tabs/ReactFlowTab'; 
import ProjectHeaderEditor from './ProjectHeaderEditor';
import PopUpMsg from './PopUpMsg';
import ReloginPopup from './ReloginPopup';
import PopUpShare from './PopUpShare';

import { createRoot } from 'react-dom/client';
import TestTab from './tabs/TestTab';
import event_buffer from '../context/event_def';
import {handleAddPlot} from './utils/AddPlot';
import {handleAddTable} from './utils/AddTable';
import {handleAddHeatMap} from './utils/AddHeatMap';
import {findReactFlowTabset} from './utils/FindReactFlowTabset';

// Imports para reactflow
import { CustomNodeData } from './tabs/ReactFlowTab';
import {
    type Edge,
    type Node,
} from '@xyflow/react';
import axios from 'axios';

// Imports de utilidades
import {LoadNodes} from './utils/LoadNodes';

import {CreateComponent} from './utils/CreateComponent';
import { CreateRepresentation, RestoreRepresentation } from './utils/CreateRepresentation';
import { compile_simulation } from './utils/ProcessSimulation';
import { test_plots_change_values} from './utils/testChangeValues';
import { handleAddHeatMapVideo } from './utils/AddHeatMapVideo';

// Separator config between buttons (configurable)
const SEPARATOR_COLOR = 'rgba(0,0,0,0.2)';
const SEPARATOR_WIDTH = 2; // px
const SEPARATOR_HEIGHT = 45; // px
const SEPARATOR_GAP = 5; // px horizontal gap around separator
const separatorStyle: React.CSSProperties = {
    width: SEPARATOR_WIDTH,
    height: SEPARATOR_HEIGHT,
    background: SEPARATOR_COLOR,
    margin: `0 ${SEPARATOR_GAP}px`,
    borderRadius: 1,
};

const separatorStyle_h: React.CSSProperties = {
    width:  '90%',
    height: SEPARATOR_WIDTH,
    background: '#8d393950',
    margin: `0 ${SEPARATOR_GAP}px`,
    borderRadius: 1,
};


const defaultNodes: Node<CustomNodeData>[] = [
    {
        id: '1',
        position: { x: 0, y: 0 },
        data: {
            label: 'Input',
            inputs: [{ name: 'input1', type: 'default' }],
            outputs: [{ name: 'output1', type: 'default' }],
        },
    },
    {
        id: '2',
        position: { x: 100, y: 100 },
        data: {
            label: 'Process',
            inputs: [{ name: 'input1', type: 'default' }],
            outputs: [{ name: 'output1', type: 'default' }],
        },
    },
    {
        id: '3',
        position: { x: 200, y: 200 },
        data: {
            label: 'Output',
            inputs: [{ name: 'input1', type: 'default' }],
            outputs: [{ name: 'output1', type: 'default' }],
        },
    },
];

const defaultEdges: Edge[] = [];

function openInNewTab(url: string): void {
  // Open the URL in a new tab
  const newTab = window.open(url, '_blank');
  
  // Optional: focus on the new tab if it opened successfully
  if (newTab) {
    newTab.focus();
  }
}

interface ProjectPageProps {
    userProfile: any;
    onLogout: () => void;
    onBackToDashboard: () => void;
    projectId: number;
    projectName: string;
    projectDescription: string;
}

export const ProjectPageB: React.FC<ProjectPageProps> = ({
    userProfile,
    onLogout,
    onBackToDashboard,
    projectId,
    projectName,
    projectDescription,
}) => {
    
    const debug_layout : boolean = config.DEBUG_LAYOUT;

    const { documentData, setDocumentData } = useDocumentData();
    const [showTableModal, setShowTableModal] = useState(false);
    const [showComponentModal, setShowComponentModal] = useState(false);
    const [showRepresentationModal, setShowRepresentationModal] = useState(false);
    const [showAboutModal, setShowAboutModal] = useState(false);
    const [showSharePopup, setShowSharePopup] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const [showPopup, setShowPopup] = useState(false);
    const [popupTitle, setPopupTitle] = useState('');
    const [popupDescription, setPopupDescription] = useState('');
    const [isSimulating, setIsSimulating] = useState(false);
    const [showRelogin, setShowRelogin] = useState(false);
    const [reloginMessage, setReloginMessage] = useState('');
    const [simulationTime, setSimulationTime] = useState<number | null>(null);
    const [total_simulation_time_last_month, setTotalSimulationTimeLastMonth] = useState<number | null>(null);

    // States for react flow
    const [nodes, setNodes] = useState<Node<CustomNodeData>[]>(defaultNodes);
    const [edges, setEdges] = useState<Edge[]>(defaultEdges);
    const [selectedNodeIndex, setSelectedNodeIndex] = useState<number | null>(null);

    // States of the 3D viewer
    const [loadedModels, setLoadedModels] = useState<LoadedModel[]>([]);

    // States de los plots
    const [plots, setPlots] = useState<Map<string, any>>(documentData.plots || new Map());

    // States de las tablas
    const [tables, setTables] = useState<Map<string, any>>(documentData.tables || new Map());

    const [layoutModel, setLayoutModel] = useState<Model>(() => {
        const initialConfig: IJsonModel = {
            global: {
                splitterEnableHandle: true,
                tabEnablePopout: true,
                tabSetEnableActiveIcon: true,
                tabSetMinWidth: 130,
                tabSetMinHeight: 100,
                tabSetEnableTabScrollbar: true,
                borderMinSize: 80,
                borderEnableTabScrollbar: true
            },
            borders: [
                {
                    type: "border",
                    size: 80,
                    location: "top",
                    show: true,
                    enableDrop: false,
                    children: [{
                        type: "tab",
                        name: "File",
                        component: "ToolBar",
                        enableClose: false,
                        closeType : 2,
                        enablePopout: false,
                        maxHeight: 80,
                        icon: "file.svg",
                        enableDrag: false,
                    }]
                },
            ],
            layout: {
                type: 'row',
                children: [
                    {
                        type: 'tabset',
                        children: [
                            { type: 'tab', name: '3D Viewer', component: 'ThreeDTab', enableClose: false, icon: 'cube.svg' },
                            { type: 'tab', name: 'Simulation Logic', component: 'ReactFlowTab', enableClose: false, icon: 'route.svg' },
                            { type: 'tab', name: 'Properties', component: 'PropertyGridTab', enableClose: false, icon: 'settings.svg' },
                        ],
                    },
                ],
            },
        };
        return Model.fromJson(initialConfig);
    });

    const checkLoginPopup = (messaje:any ) => {
        if (messaje.message === 'User not logged in') {    
            setShowRelogin(true)
        }
    }

    const handleShare = async () => {
        try {
            const response = await fetch(`${config.API_BASE_URL}/share/create-share-link.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ project_id: projectId })
            });
            const data = await response.json();
            if (data.success && data.token) {
                const shareUrl = `${window.location.origin + window.location.pathname}?share_token=${data.token}`;
                try {
                    // await navigator.clipboard.writeText(shareUrl);
                    setShareUrl(shareUrl);
                    setShowSharePopup(true);
                    console.log('Share URL copied to clipboard:', shareUrl);
                } catch (e) {
                    // ignore clipboard errors
                }
                // alert('Share URL copied to clipboard:\n' + shareUrl);
            } else {
                // alert('Error creating share link: ' + (data.message || 'Unknown error'));
                console.log('Error creating share link:', data.message || 'Unknown error');
                console.log('Response data:', data);
            }
        } catch (err) {
            // alert('Network error creating share link');
            console.log(`Network error creating share link: ${err}`);

        }
    };

    // Configuración global de axios para incluir credenciales
    axios.defaults.withCredentials = true;

    const factory = (node: any) => {
        const component = node.getComponent();
        const tabName = node.getName();
        const tabId = node.getId();

        // console.log("Rendering component:", node);
        if (component === 'ThreeDTab') {
            return <ThreeDTab loadedModels={loadedModels} setLoadedModels={setLoadedModels} setNodes={setNodes} nodes={nodes} node={node} />;
        } else if (component === 'ReactFlowTab') {
            return (
                <ReactFlowTab
                    nodes={nodes}
                    setNodes={setNodes}
                    edges={edges}
                    setEdges={setEdges}
                    selectedNodeIndex={selectedNodeIndex}
                    setSelectedNodeIndex={setSelectedNodeIndex}
                />
            );
        } else if (component === 'PropertyGridTab') {
            return (
                <PropertyGridTab
                    nodes={nodes}
                    setNodes={setNodes}
                    selectedNodeIndex={selectedNodeIndex}
                    setSelectedNodeIndex={setSelectedNodeIndex}
                    loadedModels={loadedModels}
                    setLoadedModels={setLoadedModels}
                />
            );
        } else if (component === 'PlotlyTab') {
            // Tengo que usar el tabName para averiguar a qué plot corresponde
            return (
                <PlotlyTab
                    plotIndex={tabId}
                    plots={plots}
                    setPlots={setPlots}
                    node={node}
                />
            );
        } else if (component === 'HeatMapTab') {
            // Tengo que usar el tabName para averiguar a qué plot corresponde
            return (
                <HeatMapTab
                    plotIndex={tabId}
                    plots={plots}
                    setPlots={setPlots}
                    node={node}
                />
            );
        } else if (component === 'HeatMapVideoTab') {
            // Tengo que usar el tabName para averiguar a qué plot corresponde
            return (
                <HeatMapVideoTab
                    plotIndex={tabId}
                    plots={plots}
                    setPlots={setPlots}
                    node={node}
                />
            );
        } else if (component === 'TableTab') {
            // Tengo que usar el tabName para averiguar a qué plot corresponde
            return (
                <TableTab
                    tableIndex={tabId}
                    tables={tables}
                    setTables={setTables}
                    node={node}
                />
            );
        } else if (component === 'ToolBar') {
            return(
                <header style={headerStyle}>
                    <ProjectHeaderEditor />
                    <div style={headerRight}>

                        {debug_layout && (
                            <>
                                {/* <button onClick={() => test_plots_change_values(setPlots)}>
                                    Test plot
                                </button>
                                <button onClick={() => setShowTableModal(true)} >
                                    Insert Table
                                </button>

                                <button onClick={() => handleAddTable(setDocumentData, setTables, layoutModel, setLayoutModel, "jhjkdfg", "Table 1")} >
                                    Insert TabTable
                                </button>

                                <button onClick={() => setShowRelogin(true)} >
                                    Pop up login
                                </button> */}


                                <button onClick={() => handleAddHeatMap(setDocumentData, setPlots, layoutModel, setLayoutModel, "uniqueId", "newNodeLabel")} >
                                    Add heatmap
                                </button>

                                <button onClick={() => handleAddHeatMapVideo(setDocumentData, setPlots, layoutModel, setLayoutModel, "uniqueId", "newNodeLabel")} >
                                    Add video heatmap
                                </button>
                                
                            </>
                        )}

                        <label style={{marginLeft: 10, marginRight: 10, fontSize: '0.8em', fontWeight: 500, whiteSpace: 'pre-line', lineHeight: 1.9}}>
                            {simulationTime !== null ? `Last simulation time: ${simulationTime.toFixed(1)} ms` : '---'}
                            {total_simulation_time_last_month !== null ? `\n30-day simulation time: ${(total_simulation_time_last_month/1000).toFixed(2)} s` : '\n---'}
                        </label>
                        <div style={separatorStyle} />
                        <button
                            onClick={run_simulation}
                            className="menubar-trigger"
                            id="simulate-button"
                            disabled={isSimulating}
                            style={{
                                height: '54px',
                                opacity: isSimulating ? 0.6 : 1,
                                cursor: isSimulating ? 'not-allowed' : 'pointer',
                                gap: 8, 
                                display: 'flex', 
                                alignItems: 'center'
                            }}
                        >
                            <PlayIcon/> Simulate
                        </button>
    
                        <div style={separatorStyle} />
                        <Menubar.Root className="menubar" style={{zIndex: 10,  fontSize: '0.95em', fontWeight: 500}}>
                            <Menubar.Menu>
                                <Menubar.Trigger className="menubar-trigger">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '44px'}}>
                                        <PlusCircledIcon/>  Tools <CaretDownIcon style={{ verticalAlign: 'middle', marginLeft: 10 }}/>
                                    </div>
                                </Menubar.Trigger>
                                <Menubar.Content className="menubar-content">
                                    <Menubar.Item className="menubar-item" onSelect={() => setShowComponentModal(true)}>
                                        Add simulation component
                                    </Menubar.Item>

                                    <Menubar.Item className="menubar-item" onSelect={() => setShowTableModal(true)}>
                                        Add data empirical table
                                    </Menubar.Item>

                                    <Menubar.Item className="menubar-item" onSelect={() => setShowRepresentationModal(true)}>
                                        Add 3D representation
                                    </Menubar.Item>

                                </Menubar.Content>
                            </Menubar.Menu>
                        {/* </Menubar.Root>
                        
                        <Menubar.Root className="menubar" style={{zIndex: 10,  fontSize: '0.95em', fontWeight: 500}}> */}
                            <Menubar.Menu>
                                <Menubar.Trigger className="menubar-trigger">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '44px'}}>
                                        <FileIcon/>  File <CaretDownIcon style={{ verticalAlign: 'middle', marginLeft: 10 }}/>
                                    </div>
                                </Menubar.Trigger>
                                <Menubar.Content className="menubar-content">
                                    <Menubar.Item className="menubar-item" onSelect={handleUpload}>
                                        <UploadIcon style={{marginRight: 8}}/> Upload
                                    </Menubar.Item>

                                    <Menubar.Item className="menubar-item" onSelect={handleSave}>
                                        <FileIcon style={{marginRight: 8}}/> Save on server
                                    </Menubar.Item>

                                    <Menubar.Item className="menubar-item" onSelect={handleDownloadSave}>
                                        <DownloadIcon style={{marginRight: 8}}/> Download
                                    </Menubar.Item>

                                    <Menubar.Separator className="menubar-separator" />
                                    <div style={separatorStyle_h} />

                                    <Menubar.Item className="menubar-item" onSelect={handleShare}>
                                        <Share1Icon style={{marginRight: 8}}/> Share
                                    </Menubar.Item>
                                </Menubar.Content>
                            </Menubar.Menu>

                        <div style={separatorStyle} />
                            <Menubar.Menu>
                                <Menubar.Trigger className="menubar-trigger">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8}}>
                                        {userProfile?.name || 'User'}
                                        {userProfile?.picture && (
                                        <img src={userProfile.picture} alt="Profile" style={profilePicStyle} />
                                        )} 
                                        <CaretDownIcon style={{ verticalAlign: 'middle'}}/>
                                    </div>
                                    
                                </Menubar.Trigger>
                                <Menubar.Content className="menubar-content">
                                    <Menubar.Item className="menubar-item" onSelect={onBackToDashboard}>
                                        <HomeIcon style={{marginRight: 8}}/> Back to Dashboard
                                    </Menubar.Item>
                                    
                                    <Menubar.Separator className="menubar-separator" />
                                    <div style={separatorStyle_h} />

                                    <Menubar.Item className="menubar-item" onSelect={() => openInNewTab('https://www.youtube.com/@LambdaStudioIDE')}>
                                        <InfoCircledIcon style={{marginRight: 8}}/> Tutorials
                                    </Menubar.Item>
                                    
                                    <Menubar.Item className="menubar-item" onSelect={() => openInNewTab('https://docs.google.com/forms/d/e/1FAIpQLSdUaSWbH-5OFP0qYgF_QuWg2TTDAOU3-aAeSYm05DwSyWPmVg/viewform?usp=dialog')}>
                                        <HeartIcon style={{marginRight: 8}}/> Report Issue
                                    </Menubar.Item>
                                    <Menubar.Item className="menubar-item" onSelect={() => openInNewTab('https://docs.google.com/forms/d/e/1FAIpQLSdWYH5UN9RwFULE9ErcEA1bnQRPEcpi75CC3FPzLOvrMxhG4Q/viewform?usp=publish-editor')}>
                                        <RocketIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                        Propose Feature
                                    </Menubar.Item>

                                    <Menubar.Separator className="menubar-separator" />
                                    <div style={separatorStyle_h} />

                                    <Menubar.Item className="menubar-item" onSelect={() => setShowAboutModal(true)}>
                                        <div style={{ verticalAlign: 'middle', marginRight: 8 }} > <strong> λ </strong></div>
                                        About Lambda Studio IDE
                                    </Menubar.Item>

                                    <Menubar.Separator className="menubar-separator" />
                                    <Menubar.Item className="menubar-item" onSelect={onLogout}>
                                        <ExitIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                        Log Out
                                    </Menubar.Item>

                                    <Menubar.Separator className="menubar-separator" />
                                    <div style={separatorStyle_h} />
                                    
                                    <div style={{ textAlign : 'right', color: 'gray' }}>{userProfile?.email || 'User'}</div>
                                </Menubar.Content>
                            </Menubar.Menu> 
                        </Menubar.Root>
                    </div>
                </header>
            )
        }
        return null;
    };

    var lastProjectId = 0;
    // Actualiza documentData con los datos del proyecto al inicializar
    useEffect(() => {
        const initializeDocumentData = async () => {
            setDocumentData(prev => ({
                ...prev,
                projectId,
                projectName,
                projectDescription,
                userProfile
            }));
            await fetchDocument();
            console.log("projectId in useEffect:", projectId);
        };
        if (projectId !== lastProjectId) {
            lastProjectId = projectId;
            initializeDocumentData();
        }   
    }, [projectId]);

    // 2. Obtén los datos de los plots y parámetros de simulación
    const saveData = {
        layout: layoutModel.toJson(),
        plots: JSON.stringify(Array.from(plots.entries())),
        simulationParams: {
            nodes: nodes,
            edges: edges,
        }
    };


    const handleSave = async () => {
        // 1. Obtén el layout actual de Golden Layout
        documentData.layoutConfig = layoutModel.toJson();

        console.log("plots to save:", plots);

        
        // 3. Envía el JSON al backend
        const response = await fetch(`${config.API_BASE_URL}/auth/save-simulation-version.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                project_id: projectId,
                user_id: userProfile.id,
                simulation_parameters: saveData
            })
        });
        console.log("Guardando simulation parameters:", saveData);
        const result = await response.json();
        checkLoginPopup(result);
        console.log('Respuesta del servidor:', result);

    };

    const handleUpload = async () => {
        // Crea un input file oculto y lo dispara
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.style.display = 'none';

        input.onchange = async (e: Event) => {
            const target = e.target as HTMLInputElement;
            const file = target.files?.[0];
            if (!file) return;

            try {
                const content = await file.text();
                console.log('Archivo seleccionado:', file.name);
                console.log('Contenido leído:', content);

                // llamar a loadFile
                // Aquí se debería invocar la función que procesa el contenido del archivo, por ejemplo:
                // loadFile(content)
                const contentObj = {simulation_parameters: JSON.parse(content), success: true};
                await load_model_form_data(contentObj);
            } catch (err) {
                console.error('Error leyendo archivo:', err);
            } finally {
                // limpieza del input del DOM
                if (input.parentNode) document.body.removeChild(input);
            }
        };

        document.body.appendChild(input);
        input.click();
    }

    /**
     * This function fetches the latest saved document from the backend
     * and updates the state accordingly.
     */
    const fetchDocument = async () => {
        const response = await fetch(`${config.API_BASE_URL}/auth/get-latest-simulation-version.php?project_id=${projectId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        });
        const data = await response.json();
        checkLoginPopup(data);
        await load_model_form_data(data);

        const sim_last_month = await fetch(`${config.API_BASE_URL}/simulation/front-get-simulation-stats.php?project_id=${projectId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        }).then(res => res.json());

        console.log("Total simulation time last month:", sim_last_month);
        if (sim_last_month.success) {
            setTotalSimulationTimeLastMonth(Number(sim_last_month.total_simulation_time_last_month));
        }
    };

    const load_model_form_data = async ( data:any) => {

        console.log("datasuccess", data.success);

        if (data.success == false) {
            console.warn('No saved simulation version found.');
            data.simulation_parameters = defaultConfig;
            data.success = true;
        }
        console.log('Simulation data fetched:', data);
    
        if ((data.success == true) && data.simulation_parameters) {    
            if (data.simulation_parameters.layout) {
                setDocumentData(prev => ({
                    ...prev,
                    layoutConfig: data.simulation_parameters.layout
                }));
                setLayoutModel(Model.fromJson(data.simulation_parameters.layout));
            }
        
            LoadNodes(data.simulation_parameters.simulationParams.nodes, 
                        data.simulation_parameters.simulationParams.edges,
                        setNodes, setEdges);
            if (data.simulation_parameters.plots) {
                console.log("Cargando plots:", data.simulation_parameters.plots);

                // Deserializar Map
                const restored = new Map<string, any>(JSON.parse(data.simulation_parameters.plots));

                const loadedPlots = new Map<string, any>();
                restored.forEach((plot: any, key: string) => {
                    loadedPlots.set(key, plot);
                });
                setPlots(loadedPlots);
            }

            // Cargar modelos 3D si existen
            // data.simulation_parameters.simulationParams.
            await RestoreRepresentation(data.simulation_parameters.simulationParams.nodes, setLoadedModels);
            console.log("Restored 3D models:", loadedModels);
        }
    }
    

    const handleDownloadSave = async () => {
        const content = JSON.stringify(saveData, null, 2);
        const blob = new Blob([content], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');  
        a.href = url;
        a.download = `${projectName}_simulation.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    
    const run_simulation = useCallback(async () => {
        //documentData.layoutConfig = layoutModel.toJson();

        console.log("projectId in run_simulation:", projectId);
        setIsSimulating(true);
        try {
            const responseb = await axios.post(
                `${config.API_BASE_URL}/simulation/front-set-simulation.php?project_id=${projectId}`,
                {
                    project_id: projectId,
                    user_id: userProfile.id,
                    simulation_parameters: saveData
                },
                {
                    // headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                }
            );
            var i = 0;
            let response : any = undefined;
            for (i = 0; i < 20; i++) {
                const responseC = await axios.post(
                    `${config.API_BASE_URL}/simulation/front-get-simulation.php?project_id=${projectId}`,
                    {
                        project_id: projectId,
                        user_id: userProfile.id,
                    },
                    {
                        // headers: { 'Content-Type': 'application/json' },
                        withCredentials: true
                    }
                );
                console.log("Intento ", i, " respuesta:", responseC.data);
                if (responseC.data.resultado){
                    response = responseC;   
                    break;
                }
                await new Promise(resolve => setTimeout(resolve, 500)); // Espera 200ms antes del siguiente intento
            } 

            setIsSimulating(false);
            if (response === undefined) {
                setPopupTitle('Simulation time expired');
                    setPopupDescription('Cannot retrieve simulation results within the expected time. Please try again later. If the problem persists is probably due to simulation worker overload or connection issues between the simulation engine and backend.');
                    setShowPopup(true);
                return;
            } 

            console.log("Guardando simulation parameters:", saveData);
            console.log('Respuesta del servidor:', response.data);
            if (response.data.resultado) {
                //let resultado = JSON.parse(response.data.resultado);
                //console.log('Simulation started successfully', resultado);
                compile_simulation(response.data.resultado, setNodes, setLoadedModels, setPlots, setTables);
                setSimulationTime(response.data.simulation_time);
                // let value_to_add = Number(response.data.simulation_time) ?? 0;
                // let prev_total = total_simulation_time_last_month ?? 0;
                // setTotalSimulationTimeLastMonth(total_simulation_time_last_month + value_to_add);
                // setTotalSimulationTimeLastMonth(response.data.simulation_time + total_simulation_time_last_month );
                setTotalSimulationTimeLastMonth(prev => {
                        const prevVal = Number(prev ?? 0);
                        const addVal = Number(response.data.simulation_time ?? 0);
                        return prevVal + addVal;
                    });
                handleSave(); // Auto-save after simulation
            } else {
                if (response.data.status === 'Time_expired') {
                    setPopupTitle('Simulation time expired');
                    setPopupDescription('Cannot retrieve simulation results within the expected time. Please try again later. If the problem persists is probably due to simulation worker overload or connection issues between the simulation engine and backend.');
                    setShowPopup(true);
                }
            }
            checkLoginPopup(response.data);
        } catch (error) {
            console.error('Error al ejecutar la simulación:', error);
        } finally {
            setIsSimulating(false);
        }
    }, [projectId, userProfile?.id, layoutModel, plots, nodes, edges]);

    // Añade listener global para F5: ejecuta run_simulation y evita la recarga por defecto
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Algunas plataformas/reportes usan "F5" en e.key; asegurar captura
            if (e.key === 'F5') {
                e.preventDefault();
                run_simulation();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [run_simulation]);

    

    // Header igual que antes
    return (
        <div style={dashboardContainerStyle}>
            <Layout model={layoutModel} factory={factory} /> 
            {showTableModal && (
                <CategorySelectorModal
                    onClose={() => setShowTableModal(false)}
                    onSelect={component => {
                        setShowTableModal(false);
                        console.log('Selected component:', component);
                        CreateComponent(component, setNodes, setLoadedModels, setPlots, layoutModel, setLayoutModel, setDocumentData, setTables);
                    }}
                />
            )}
            {showComponentModal && (
                <ComponentSelectorModal
                    onClose={() => setShowComponentModal(false)}
                    onSelect={component => {
                        setShowComponentModal(false);
                        console.log('Selected component:', component);
                        CreateComponent(component, setNodes, setLoadedModels, setPlots, layoutModel, setLayoutModel, setDocumentData, setTables);
                    }}
                />
            )}
            {showRepresentationModal && (
                <RepresentationSelectorModal
                    onClose={() => setShowRepresentationModal(false)}
                    onSelect={representation => {
                        setShowRepresentationModal(false);
                        // console.log('Selected representation:', representation);
                        CreateRepresentation(representation, setNodes, setLoadedModels);
                    }}
                />
            )}
            {showAboutModal && (
                <AboutModal
                    onClose={() => setShowAboutModal(false)}
                />
            )}
            {showPopup && (
                <PopUpMsg
                    isOpen={showPopup}
                    title={popupTitle}
                    description={popupDescription}
                    onAccept={() => setShowPopup(false)}
                    onClose={() => setShowPopup(false)}
                    acceptLabel="OK"
                    cancelLabel="Close"
                />
            )}
            {showRelogin && (
                <ReloginPopup
                    isOpen={showRelogin}
                    message={reloginMessage}
                    onClose={() => setShowRelogin(false)}
                />
            )}
            {showSharePopup && (
                <PopUpShare
                    title={"Share Project"}
                    description={"Everyone with the link can view this project. Users using this link will get a copy of the latest state of this project in their account. You can share this link with your collaborators or anyone you want to show your project to. \n \n To remove sharing, go to the dashboard -> Shared and click 'Remove share'."}
                    onClose={() => setShowSharePopup(false)}
                    url={shareUrl}
                />
                    
            )}
        </div>
    );
};

export default ProjectPageB;

